import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router";
import {
  workspaceService,
  CreateWorkspacePayload,
  UpdateWorkspacePayload,
} from "@/services/workspaceService";
import { Workspace } from "@/components/UI/TableToolbar/WorkspaceButton/workspace";
import { useScopeParams } from "./useScopeParams";

export const useWorkspaceMutations = (
  tableName: string,
  dataSection: string = "all",
) => {
  const { scope, id } = useScopeParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isBetaMode = location.pathname.includes("/beta/");

  const queryKey = ["workspaces", id, tableName, dataSection];

  const createMutation = useMutation({
    mutationFn: async (payload: CreateWorkspacePayload) => {
      return await workspaceService.createWorkspace(scope, id, {
        ...payload,
        data_section: dataSection === "all" ? undefined : dataSection,
      });
    },
    onSuccess: (newWorkspace) => {
      queryClient.setQueryData(queryKey, (oldData: Workspace[] = []) => [
        ...oldData,
        newWorkspace,
      ]);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      workspaceId,
      payload,
    }: {
      workspaceId: number;
      payload: UpdateWorkspacePayload;
    }) => {
      if (isBetaMode) {
        return { id: workspaceId, ...payload } as Workspace;
      }
      return await workspaceService.updateWorkspace(
        scope,
        id,
        workspaceId,
        payload,
      );
    },
    onSuccess: (updatedWorkspace) => {
      queryClient.setQueryData(queryKey, (oldData: Workspace[] = []) =>
        oldData.map((w) =>
          w.id === updatedWorkspace.id ? updatedWorkspace : w,
        ),
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (workspaceId: number) => {
      await workspaceService.deleteWorkspace(scope, id, workspaceId);
      return workspaceId;
    },
    onSuccess: (deletedId) => {
      queryClient.setQueryData(queryKey, (oldData: Workspace[] = []) =>
        oldData.filter((w) => w.id !== deletedId),
      );
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async ({
      workspace,
      newTitle,
    }: {
      workspace: Workspace;
      newTitle: string;
    }) => {
      return await workspaceService.duplicateWorkspace(
        scope,
        id,
        workspace,
        newTitle,
      );
    },
    onSuccess: (newWorkspace) => {
      if (isBetaMode) {
        queryClient.setQueryData(queryKey, (oldData: Workspace[] = []) => [
          ...oldData,
          newWorkspace,
        ]);
      } else {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  return {
    createWorkspace: createMutation.mutate,
    createWorkspaceAsync: createMutation.mutateAsync,
    updateWorkspace: updateMutation.mutate,
    updateWorkspaceAsync: updateMutation.mutateAsync,
    deleteWorkspace: deleteMutation.mutate,
    duplicateWorkspace: duplicateMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isDuplicating: duplicateMutation.isPending,
  };
};
